/**
 * "Empty trash" (`100-settings.md` §5 → Danger zone; roadmap M7 criterion 4).
 *
 * > "Permanently deletes every archived page and its descendants, and enqueues asset GC. Confirm
 * > dialog requires typing the number of pages to be deleted. Irreversible, and the dialog says
 * > so in those words."
 *
 * `POST /api/trash/empty` — one request, one outbox entry, and the server does the cascade and
 * the asset GC (`062` §5). Not N `DELETE /api/pages/{id}` calls: the descendants go with their
 * root through the FK cascade, so a client that queued a delete per archived page would spend
 * most of them 404-ing rows the previous one had already taken.
 *
 * It is an outbox entry like every other write, so emptying the trash offline is queued, durable
 * and drains later — which is exactly right, and free.
 *
 * The typed number is the guard. Not a checkbox and not a hold-to-confirm: the count is the one
 * fact the user must have read, so reading it is the price of the button.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { keys } from '@/api/keys';
import type { TreePage, TreeResponse } from '@/api/endpoints/pages';
import { outbox } from '@/offline/outbox';
import { translate, useTranslation } from '@/lib/i18n';

/**
 * One entry, one entity id. `trash.empty` is deliberately NOT in the outbox's coalescable set:
 * emptying a trash that has since gained a page is a different act from the one that was queued,
 * and merging the two would be merging two different intents.
 */
function useEmptyTrash() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      outbox.enqueue({
        kind: 'trash.empty',
        entityId: 'trash',
        label: 'empty trash',
        request: { method: 'POST', path: '/api/trash/empty' },
      }),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: keys.pages.trash() });
      const previous = queryClient.getQueryData<TreeResponse>(keys.pages.trash());

      // The trash really is empty now, on this device — the entry is durable in IndexedDB. A
      // list that keeps showing the pages until the network answers reads as a broken button.
      queryClient.setQueryData<TreeResponse>(keys.pages.trash(), { pages: [] });

      return { previous };
    },

    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(keys.pages.trash(), context.previous);
      toast.error(translate('notebook.emptyTrash.error'));
    },
  });
}

export function EmptyTrashDialog({
  open,
  onOpenChange,
  pages,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Every archived page — roots and cascaded descendants. The COUNT is all of them. */
  pages: readonly TreePage[];
}) {
  const { t, plural } = useTranslation();
  const [typed, setTyped] = useState('');
  const { mutate: empty } = useEmptyTrash();

  const total = pages.length;
  const confirmed = typed.trim() === String(total);

  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="empty-trash-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{plural('notebook.emptyTrash.title', total)}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('notebook.emptyTrash.bodyBefore')}
            <strong>{t('notebook.emptyTrash.bodyStrong')}</strong>
            {t('notebook.emptyTrash.bodyAfter')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="empty-trash-confirm">
            {t('notebook.emptyTrash.confirmBefore')}
            <span className="font-mono font-semibold">{total}</span>
            {t('notebook.emptyTrash.confirmAfter')}
          </Label>
          <Input
            id="empty-trash-confirm"
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
            }}
            inputMode="numeric"
            autoComplete="off"
            data-testid="empty-trash-confirm"
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={!confirmed || total === 0}
            data-testid="empty-trash-submit"
            onClick={() => {
              empty();
              toast.success(plural('notebook.emptyTrash.deletingToast', total));
              onOpenChange(false);
            }}
          >
            {t('notebook.trash.deletePermanentlyTitle')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
